import { api } from '@/helpers/apiClient'
import { toQueryString } from '@/helpers/queryString'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  siteVisitDtoSchema,
  siteVisitListDtoSchema,
  type SiteVisitDto,
  type SiteVisitListDto,
} from '@lib/dto/site-visit.dto'
import {
  recordHistoryDtoSchema,
  siteHistoryDtoSchema,
  siteMapAsOfDtoSchema,
  type RecordHistoryDto,
  type SiteHistoryDto,
  type SiteMapAsOfDto,
} from '@lib/dto/site-history.dto'
import type { SiteChangeEntity } from '@lib/enums/site-change.enum'
import type { StartSiteVisitInput } from '@lib/input/site-visit.input'
import type { ISiteVisitService } from '@/types/SiteVisitService.types'

/** Service for a site's visits. See docs/site-visits.md. */
class SiteVisitService implements ISiteVisitService {
  async list(siteId: string): Promise<SiteVisitListDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/visits`,
        siteVisitListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list site visits', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** A site's visits and the changes to its record, newest first. */
  async history(siteId: string): Promise<SiteHistoryDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/history`,
        siteHistoryDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to read a site's history", {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** One record's history: its changes, work done, and each visit's findings. */
  async recordHistory(
    siteId: string,
    entity: SiteChangeEntity,
    recordId: string,
  ): Promise<RecordHistoryDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/records/${encodeURIComponent(recordId)}/history${toQueryString({ entity })}`,
        recordHistoryDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to read a record's history", {
        siteId,
        recordId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /** A site as it stood on a visit's day, with what the visit found. */
  async mapAsOf(visitId: string): Promise<SiteMapAsOfDto> {
    try {
      return await api.get(
        `/site-visits/${encodeURIComponent(visitId)}/map`,
        siteMapAsOfDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to read a site as of a visit', {
        visitId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Take back a record of tree work that was not done. A removal it recorded
   * puts the tree back on the site.
   */
  async undoTreeWork(id: string): Promise<void> {
    try {
      await api.del(`/tree-work/${encodeURIComponent(id)}`)
    } catch (error) {
      logger.error('Failed to take back a record of tree work', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Start a visit, or join the one already started that day. Sent again until
   * it lands, so the answer may be a visit with a different id: the one this
   * phone's was joined into.
   */
  async start(
    siteId: string,
    input: StartSiteVisitInput,
  ): Promise<SiteVisitDto> {
    try {
      return await api.post(
        `/sites/${encodeURIComponent(siteId)}/visits`,
        input,
        siteVisitDtoSchema,
      )
    } catch (error) {
      // Warn, not error: offline is the normal reason, and it is sent again.
      logger.warn('Starting a site visit did not reach the server', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setFinished(id: string, finished: boolean): Promise<SiteVisitDto> {
    try {
      return await api.post(
        `/site-visits/${encodeURIComponent(id)}/${finished ? 'finish' : 'reopen'}`,
        {},
        siteVisitDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to change whether a visit is finished', {
        id,
        finished,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const siteVisitService: ISiteVisitService = new SiteVisitService()
